// Storemapper marks the flagship location with tier 1; every other store is tier 2.
const FLAGSHIP_TIER = 1;

const LIST_ID = "storemapper-list";
const WIDGET_ID = "storemapper";
const PINNED_CLASS = "store-locator-flagship";

// Storemapper's `UserData.class_selected`, which the stylesheet also hardcodes.
const SELECTED_CLASS = "strmpr-selected";

// Storemapper renders its list client-side, so give up waiting for it rather than leave an
// observer running on a page where the app never loads.
const WIDGET_TIMEOUT = 30000;

function element(tag, className, { text, ...attributes } = {}) {
  const node = document.createElement(tag);
  node.className = className;
  if (text !== undefined) node.textContent = text;
  for (const [name, value] of Object.entries(attributes)) {
    node.setAttribute(name, value);
  }
  return node;
}

/**
 * Reads the flagship out of Storemapper's own dataset rather than the rendered list, so it is
 * available on the searches that filter it out of the results entirely.
 */
function findFlagship() {
  const features = window.Storemapper?.stores?.features;
  return features?.find((feature) => Number(feature.properties.tier) === FLAGSHIP_TIER) ?? null;
}

// Mirrors the category test Storemapper filters its own results with, so the flagship drops out of
// the list whenever a filter that excludes its category is active.
function matchesFilters(store) {
  const selected = window.Storemapper.valid_category_array;
  if (!selected?.length) return true;

  const tags = store.tag_ids ?? [];
  return window.Storemapper.UserData.uses_inclusive_filtering
    ? selected.some((tag) => tags.includes(tag))
    : selected.every((tag) => tags.includes(tag));
}

// Mirrors the URL Storemapper builds for its own Directions links, including the `<br>` it strips
// out of multi-line addresses.
function directionsUrl(store) {
  if (store.custom_directions_url) return store.custom_directions_url;
  if (!store.address) return store.directions_url || "";

  const destination = encodeURIComponent(store.address)
    .replace(/'/g, "%27")
    .replace(/%3Cbr%3E/g, "");

  return `https://maps.google.com/maps?saddr=&daddr=${destination}`;
}

/**
 * The category marker the boutique pin styling keys off. Storemapper holds it as an HTML
 * fragment in a custom field, so it is parsed inertly and only the attribute is carried across.
 */
function buildResultType(store) {
  const value = (store.store_custom_fields ?? []).map((field) => field.value).find(Boolean);
  const parsed = value && new DOMParser().parseFromString(value, "text/html");
  const source = parsed && parsed.querySelector("[data-result-type]");
  if (!source) return null;

  const wrapper = element(
    "div",
    "strmpr-field-custom storemapper-custom store-locator-result-type",
  );
  wrapper.append(element("div", "", { "data-result-type": source.dataset.resultType }));
  return wrapper;
}

/**
 * Rebuilds Storemapper's list-card markup for one store. Storemapper renders only the first page
 * of a result set, so the flagship's own card is missing from most searches and there is nothing
 * to clone — these are the fields its list template emits that the theme stylesheet shows.
 */
function buildCard(feature) {
  const store = feature.properties;
  const [lng, lat] = feature.geometry.coordinates;
  const { view_on_map_text, directions_button_text, website_text } = window.Storemapper.UserData;
  const name = String(store.name).trim();

  const card = element("div", `strmpr-search-result tier${store.tier} ${PINNED_CLASS}`, {
    id: `storemapper-listing-${store.idx}`,
    "data-idx": store.idx,
    "data-db": store.id,
    role: "listitem",
    "aria-label": `${name}, ${store.address}`,
  });

  // Storemapper nests the fields in this wrapper and the theme flattens it with
  // `display: contents` so the card grid can place them itself.
  const bucket = element("div", "strmpr-field-bucket-general");
  card.append(bucket);

  bucket.append(
    element("div", "strmpr-field-name", { text: name }),
    element("div", "strmpr-field-address", {
      text: store.address,
      "aria-label": "Location Address",
    }),
  );

  if (store.address || store.custom_directions_url || store.directions_url) {
    const label = `${directions_button_text} directions for ${name} from store list`;
    const directions = element("div", "strmpr-field-directions storemapper_directions");
    directions.append(
      element("a", "", {
        text: directions_button_text,
        href: directionsUrl(store),
        target: "_blank",
        "aria-label": `${label} - opens in a new window`,
      }),
    );
    bucket.append(directions);
  }

  if (store.phone) {
    const phone = element("div", "strmpr-field-phone");
    phone.append(
      element("a", "", {
        text: store.phone,
        href: `tel:${store.phone}`,
        "aria-label": `${store.phone} phone number for ${name} - opens phone app`,
      }),
    );
    bucket.append(phone);
  }

  if (store.url) {
    const url = element("div", "strmpr-field-url");
    url.append(
      element("a", "", {
        text: website_text || store.url,
        href: store.url,
        target: "_blank",
        "aria-label": `${website_text} website for ${name} - opens in a new window`,
      }),
    );
    bucket.append(url);
  }

  const resultType = buildResultType(store);
  if (resultType) bucket.append(resultType);

  // Storemapper delegates clicks on .storemapper-storelink from the widget root and resolves the
  // store from data-idx alone, so this drives the map exactly as a real card's pin does.
  card.append(
    element("button", "storemapper_view strmpr-field-show-on-map storemapper-storelink", {
      text: view_on_map_text,
      type: "button",
      "data-idx": store.idx,
      "data-lng": lng,
      "data-lat": lat,
      "aria-label": `View on the map: '${name}'`,
    }),
  );

  return card;
}

/**
 * Moves the flagship to the head of the results list, whether or not the search returned it.
 * Storemapper's own card is preferred when it is there — it carries the search distance — and the
 * standalone card stands in when it is not, so only one of the two is ever in the document at a
 * time and the id Storemapper selects listings by stays unique.
 */
function pinFlagship(list, card, store) {
  // A render clears the highlight off every card Storemapper rebuilds. This one is detached while
  // that happens, out of reach of both the deselect and the reselect that follow it, so it has to
  // be cleared here or it keeps a border the map no longer agrees with.
  card.classList.remove(SELECTED_CLASS);

  const rendered = list.querySelector(
    `.strmpr-search-result[data-idx="${card.dataset.idx}"]:not(.${PINNED_CLASS})`,
  );
  const flagship = rendered ?? (matchesFilters(store) ? card : null);

  if (flagship !== card) card.remove();
  if (flagship && list.firstElementChild !== flagship) list.prepend(flagship);
}

function start() {
  const list = document.getElementById(LIST_ID);
  const flagship = findFlagship();
  if (!list || !flagship) return false;

  const card = buildCard(flagship);
  const reconcile = () => pinFlagship(list, card, flagship.properties);

  // Every search, filter and "view more stores" replaces the list wholesale, which drops the card
  // and can bring Storemapper's own copy of the flagship back with it.
  new MutationObserver(reconcile).observe(list, { childList: true });
  reconcile();

  return true;
}

if (!start()) {
  const widget = document.getElementById(WIDGET_ID);

  if (widget) {
    const pending = new MutationObserver(() => {
      if (start()) pending.disconnect();
    });

    pending.observe(widget, { childList: true, subtree: true });
    setTimeout(() => pending.disconnect(), WIDGET_TIMEOUT);
  }
}
